import { useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowLeft,
  BriefcaseBusiness,
  Camera,
  Clapperboard,
  LayoutTemplate,
  Smartphone,
  SquareDashed,
  UserRound,
} from "lucide-react";
import {
  SiteThemePicker,
  STARTER_THEMES,
  siteService,
  type SiteStart,
  type SiteTemplate,
  type SiteThemeId,
} from "../features";
import { LogOutButton } from "../components/auth/LogOutButton";
import { Badge, Button, Card, Input, Notice, Textarea } from "../components/ui";

const templates: Array<{
  id: SiteTemplate;
  name: string;
  description: string;
  icon: typeof BriefcaseBusiness;
}> = [
  {
    id: "portfolio",
    name: "Design & freelance",
    description: "Showcase selected projects and your best work.",
    icon: BriefcaseBusiness,
  },
  {
    id: "photography",
    name: "Photography",
    description: "Start with a gallery for your photographs and series.",
    icon: Camera,
  },
  { id: "apps", name: "Apps & products", description: "Introduce your apps and the ideas behind them.", icon: Smartphone },
  { id: "video", name: "Video & YouTube", description: "Share your films, videos, and their stories.", icon: Clapperboard },
  {
    id: "personal",
    name: "Something else",
    description: "Introduce yourself, your interests, and what you do.",
    icon: UserRound,
  },
];

/** The page each example site lists its work on (as seeded by create_onboarding_site). */
const EXAMPLE_LISTING: Record<SiteTemplate, string> = {
  portfolio: "Projects",
  photography: "Series",
  apps: "Apps",
  video: "Films",
  personal: "Notes",
};

/** What each way of starting gives you, in the words of the chosen kind of site. */
const starts = (template: SiteTemplate): Array<{
  id: SiteStart;
  name: string;
  description: string;
  icon: typeof LayoutTemplate;
  badge?: string;
}> => [
  {
    id: "example",
    name: "Start with an example",
    description: `Home, ${EXAMPLE_LISTING[template]}, About and Contact pages, filled with sample words and pictures to swap for your own.`,
    icon: LayoutTemplate,
    badge: "Easiest",
  },
  {
    id: "blank",
    name: "Start blank",
    description: "Just a Home page with your name on it. Add pages and blocks as you go.",
    icon: SquareDashed,
  },
];

/** One option in a list of choices: a radio button styled as a card. */
const ChoiceCard = ({
  name,
  checked,
  onChange,
  icon: Icon,
  title,
  description,
  badge,
  value,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  icon: typeof LayoutTemplate;
  title: string;
  description: ReactNode;
  badge?: string;
  value: string;
}) => (
  <label
    className={`flex cursor-pointer items-center gap-4 rounded-2xl border p-4 transition has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-hazumu-ink ${checked ? "border-hazumu-terracotta bg-hazumu-blush" : "border-hazumu-border bg-hazumu-white hover:border-hazumu-clay"}`}
  >
    <input checked={checked} className="sr-only" name={name} onChange={onChange} type="radio" value={value} />
    <span
      className={`flex size-12 shrink-0 items-center justify-center rounded-xl ${checked ? "bg-hazumu-terracotta text-hazumu-white" : "bg-hazumu-blush text-hazumu-terracotta"}`}
    >
      <Icon aria-hidden="true" size={22} strokeWidth={1.8} />
    </span>
    <span>
      <span className="flex flex-wrap items-center gap-2 font-semibold">
        {title}
        {badge && <Badge>{badge}</Badge>}
      </span>
      <span className="mt-1 block text-sm leading-5 text-hazumu-muted">{description}</span>
    </span>
  </label>
);

type OnboardingPageProps = {
  onComplete: (siteId: string) => void;
};

const STEPS = 4;

export const OnboardingPage = ({ onComplete }: OnboardingPageProps) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [template, setTemplate] = useState<SiteTemplate>("portfolio");
  const [start, setStart] = useState<SiteStart>("example");
  const [theme, setTheme] = useState<SiteThemeId>(STARTER_THEMES.portfolio);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const site = await siteService.createFromTemplate({
        template,
        start,
        theme,
        name,
        description,
      });
      onComplete(site.id);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not create your website.");
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-hazumu-canvas px-5 py-8 text-hazumu-ink sm:px-8 sm:py-12">
      <Card variant="hero" className="mx-auto w-full max-w-xl">
        <div className="mb-10 flex items-center justify-between text-xs font-semibold uppercase tracking-widest">
          <button
            className={`inline-flex items-center gap-1.5 rounded-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink ${step === 1 ? "invisible" : "text-hazumu-muted hover:text-hazumu-ink"}`}
            onClick={() => {
              setError("");
              setStep(step === 1 ? 1 : ((step - 1) as 1 | 2 | 3));
            }}
            type="button"
          >
            <ArrowLeft aria-hidden="true" size={14} />
            Back
          </button>
          <span className="text-hazumu-terracotta">
            Step {step} of {STEPS}
          </span>
        </div>

        {step === 1 ? (
          <div>
            <header className="mb-7">
              <h1 className="font-lora text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
                What are you <span className="italic text-hazumu-terracotta">making?</span>
              </h1>
              <p className="mt-3 leading-7 text-hazumu-muted">
                This sets up the right pages and words for what you do.
              </p>
            </header>

            <fieldset className="space-y-3">
              <legend className="sr-only">Choose a website type</legend>
              {templates.map((item) => (
                <ChoiceCard
                  key={item.id}
                  name="site-template"
                  value={item.id}
                  checked={template === item.id}
                  onChange={() => {
                    setTemplate(item.id);
                    setTheme(STARTER_THEMES[item.id]);
                  }}
                  icon={item.icon}
                  title={item.name}
                  description={item.description}
                />
              ))}
            </fieldset>

            <Button variant="primary" size="lg" fullWidth className="mt-7" onClick={() => setStep(2)}>
              Continue
            </Button>
          </div>
        ) : step === 2 ? (
          <div>
            <header className="mb-7">
              <h1 className="font-lora text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
                How would you like to <span className="italic text-hazumu-terracotta">start?</span>
              </h1>
              <p className="mt-3 leading-7 text-hazumu-muted">
                An example shows how a finished site fits together. Blank is a clean slate.
              </p>
            </header>

            <fieldset className="space-y-3">
              <legend className="sr-only">Choose how to start</legend>
              {starts(template).map((item) => (
                <ChoiceCard
                  key={item.id}
                  name="site-start"
                  value={item.id}
                  checked={start === item.id}
                  onChange={() => setStart(item.id)}
                  icon={item.icon}
                  title={item.name}
                  description={item.description}
                  badge={item.badge}
                />
              ))}
            </fieldset>

            <Button variant="primary" size="lg" fullWidth className="mt-7" onClick={() => setStep(3)}>
              Continue
            </Button>
          </div>
        ) : step === 3 ? (
          <div>
            <header className="mb-7">
              <h1 className="font-lora text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
                Pick a <span className="italic text-hazumu-terracotta">look</span>
              </h1>
              <p className="mt-3 leading-7 text-hazumu-muted">
                Sets the colours and fonts across your site. You can switch any time in Design.
              </p>
            </header>

            <SiteThemePicker name="site-theme" value={theme} onChange={setTheme} />

            <Button variant="primary" size="lg" fullWidth className="mt-7" onClick={() => setStep(4)}>
              Continue
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <header className="mb-7">
              <h1 className="font-lora text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
                Tell us the <span className="italic text-hazumu-terracotta">basics</span>
              </h1>
              <p className="mt-3 leading-7 text-hazumu-muted">
                {start === "blank"
                  ? "They go at the top of your Home page, and both can change whenever you like."
                  : "Both of these can change whenever you like."}
              </p>
            </header>

            <div className="space-y-5">
              <label className="block text-sm font-semibold">
                What is it called?
                <Input
                  autoFocus
                  className="mt-2 font-normal"
                  inputSize="lg"
                  maxLength={120}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Marlow & Co."
                  required
                  value={name}
                />
              </label>

              <label className="block text-sm font-semibold">
                Describe yourself or your business
                <Textarea
                  className="mt-2 min-h-40 resize-y font-normal leading-6"
                  inputSize="lg"
                  maxLength={500}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="A small design studio working with independent food and drink brands."
                  value={description}
                />
              </label>
            </div>

            {error && (
              <Notice tone="error" className="mt-5">
                {error}
              </Notice>
            )}

            <Button
              variant="primary"
              size="lg"
              fullWidth
              className="mt-7"
              disabled={loading || !name.trim()}
              type="submit"
            >
              {loading ? "Creating your website…" : "Create my website"}
            </Button>
          </form>
        )}
      </Card>
      <div className="mx-auto mt-4 flex max-w-xl justify-center">
        <LogOutButton />
      </div>
    </main>
  );
};
